import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, PencilSimple, TrashSimple, Truck, Factory, Storefront, Warning, FloppyDisk, MapPin } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import { titleCaseTR, toLowerTR, digitsOnly, searchMatchTR } from "@/lib/textFormat";
import { Checkbox } from "@/components/ui/checkbox";
import CityDistrictPicker from "@/components/CityDistrictPicker";

const TYPES = {
  uretici: { label: "Üretici", icon: Factory, color: "bg-[#1B4776] text-white" },
  bayi: { label: "Bayi", icon: Storefront, color: "bg-[#95C11F] text-[#1B2A3A]" },
  tedarikci: { label: "Tedarikçi", icon: Truck, color: "bg-[#8595B4] text-white" },
};

const empty = {
  name: "", supplier_type: "tedarikci", title: "", tax_office: "", tax_number: "",
  contact_person: "", phone: "", email: "", website: "", address: "",
  city: "", district: "",
  notes: "",
};

export default function Suppliers() {
  const { user } = useAuth();
  const askConfirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [unregistered, setUnregistered] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("name_asc"); // name_asc | name_desc | usage_desc | usage_asc | type
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const isAdmin = !!user;

  const load = async () => {
    const [{ data }, u] = await Promise.all([
      axios.get(`${API}/suppliers`),
      axios.get(`${API}/suppliers/unregistered`).catch(() => ({ data: [] })),
    ]);
    setRows(data);
    setUnregistered(u.data || []);
    setSelectedIds(new Set());
  };
  useEffect(() => { load(); }, []);

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = (visibleRows) => {
    setSelectedIds(new Set(visibleRows.map(r => r.id)));
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = await askConfirm({
      title: "Firmaları Sil",
      description: `${selectedIds.size} firmayı kalıcı olarak silmek istiyor musunuz? Bu işlem geri alınamaz.`,
      destructive: true,
      confirmText: "Sil",
    });
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const r = await axios.post(`${API}/suppliers/bulk-delete`, { ids: Array.from(selectedIds) });
      toast.success(`${r.data.deleted} firma silindi`);
      clearSelection();
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBulkDeleting(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) { toast.error("Firma adı zorunludur"); return; }
    // Yazım kuralları — kaydetmeden önce normalize et (legacy verilerde ekstra güvence)
    const tn = digitsOnly(form.tax_number, 11);
    if (tn && tn.length !== 10 && tn.length !== 11) {
      toast.error("VKN 10 (şirket) veya 11 (şahıs) hane olmalıdır");
      return;
    }
    const payload = {
      ...form,
      name: titleCaseTR(form.name),
      title: titleCaseTR(form.title),
      tax_office: titleCaseTR(form.tax_office),
      contact_person: titleCaseTR(form.contact_person),
      address: titleCaseTR(form.address),
      city: titleCaseTR(form.city),
      district: titleCaseTR(form.district),
      notes: titleCaseTR(form.notes),
      email: toLowerTR(form.email),
      website: toLowerTR(form.website),
      tax_number: tn,
    };
    try {
      if (editing) await axios.put(`${API}/suppliers/${editing.id}`, payload);
      else await axios.post(`${API}/suppliers`, payload);
      toast.success(editing ? "Firma güncellendi" : "Firma eklendi");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (id) => {
    const target = rows.find(r => r.id === id);
    const ok = await askConfirm({
      title: "Firmayı Sil",
      description: `"${target?.name || "Bu firma"}" verisini silmek istiyor musunuz? Bu işlem geri alınamaz.`,
      destructive: true,
      confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "", supplier_type: s.supplier_type || "tedarikci",
      title: s.title || "", tax_office: s.tax_office || "", tax_number: s.tax_number || "",
      contact_person: s.contact_person || "", phone: s.phone || "",
      email: s.email || "", website: s.website || "",
      address: s.address || "",
      city: s.city || "", district: s.district || "",
      notes: s.notes || "",
    });
    setOpen(true);
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  const openNewFromName = (name) => {
    setEditing(null);
    setForm({ ...empty, name: name || "" });
    setOpen(true);
  };

  const quickRegister = async (name) => {
    if (!name) return;
    try {
      await axios.post(`${API}/suppliers`, { ...empty, name });
      toast.success(`"${name}" firmalar listesine eklendi`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const visible = useMemo(() => {
    let out = filter === "all" ? rows : rows.filter(r => (r.supplier_type || "tedarikci") === filter);
    if (q && q.trim()) {
      out = out.filter(r => searchMatchTR(q, [
        r.name, r.title, r.tax_office, r.tax_number,
        r.contact_person, r.phone, r.email, r.website,
        r.address, r.notes,
      ].filter(Boolean).join(" ")));
    }
    // Sıralama
    const cmp = (a, b) => (a.name || "").localeCompare(b.name || "", "tr", {
      numeric: true, sensitivity: "base",
    });
    const sorted = [...out];
    if (sortBy === "name_asc") sorted.sort(cmp);
    else if (sortBy === "name_desc") sorted.sort((a, b) => cmp(b, a));
    else if (sortBy === "usage_desc") sorted.sort((a, b) =>
      (b.usage_count || 0) - (a.usage_count || 0) || cmp(a, b));
    else if (sortBy === "usage_asc") sorted.sort((a, b) =>
      (a.usage_count || 0) - (b.usage_count || 0) || cmp(a, b));
    else if (sortBy === "type") sorted.sort((a, b) =>
      (a.supplier_type || "").localeCompare(b.supplier_type || "", "tr") || cmp(a, b));
    else if (sortBy === "last_used") sorted.sort((a, b) =>
      (b.last_used_date || "").localeCompare(a.last_used_date || "") || cmp(a, b));
    return sorted;
  }, [rows, filter, q, sortBy]);

  return (
    <Shell
      title="Firmalar"
      subtitle="Tedarikçi Yönetimi"
      hideSiteSelector
      actions={
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-supplier" onClick={openNew}
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              <Plus size={16} className="mr-2" /> Yeni Firma
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:rounded-sm max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-bold tracking-tight font-heading">
                {editing ? "Firma Düzenle" : "Yeni Firma"}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Firma Adı (Kısa)</Label>
                <Input value={form.name} data-testid="supplier-name"
                       onChange={e => set("name", titleCaseTR(e.target.value))}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Firma Tipi</Label>
                <Select value={form.supplier_type} onValueChange={(v) => set("supplier_type", v)}>
                  <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="supplier-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPES).map(([v, t]) => (
                      <SelectItem key={v} value={v}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Ticari Unvan</Label>
                <Input value={form.title} onChange={e => set("title", titleCaseTR(e.target.value))}
                       placeholder="Ör: ABC İnşaat Malz. San. Tic. A.Ş."
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Vergi Dairesi</Label>
                <Input value={form.tax_office} onChange={e => set("tax_office", titleCaseTR(e.target.value))}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Vergi Kimlik No (VKN)</Label>
                <Input
                  value={form.tax_number}
                  onChange={e => set("tax_number", digitsOnly(e.target.value, 11))}
                  inputMode="numeric"
                  maxLength={11}
                  placeholder="10 haneli (şirket) / 11 haneli (şahıs)"
                  data-testid="supplier-tax-number"
                  className="mt-2 rounded-none sm:rounded-sm font-mono"
                />
                {form.tax_number && form.tax_number.length > 0 && form.tax_number.length !== 10 && form.tax_number.length !== 11 && (
                  <div className="mt-1 text-[10px] text-amber-700">
                    VKN 10 (şirket) veya 11 (şahıs) hane olmalıdır. Şu an {form.tax_number.length} hane.
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Yetkili</Label>
                <Input value={form.contact_person} onChange={e => set("contact_person", titleCaseTR(e.target.value))}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Telefon</Label>
                <Input value={form.phone} onChange={e => set("phone", e.target.value)}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">E-posta</Label>
                <Input type="email" value={form.email} onChange={e => set("email", toLowerTR(e.target.value))}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Web Sitesi</Label>
                <Input value={form.website} onChange={e => set("website", toLowerTR(e.target.value))}
                       placeholder="www.firma.com"
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Adres</Label>
                <Textarea value={form.address} onChange={e => set("address", titleCaseTR(e.target.value))}
                          className="mt-2 rounded-none sm:rounded-sm" rows={2} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">İl / İlçe</Label>
                <div className="mt-2">
                  <CityDistrictPicker
                    city={form.city}
                    district={form.district}
                    onChange={(v) => setForm(f => ({ ...f, city: v.city, district: v.district }))}
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Notlar</Label>
                <Textarea value={form.notes} onChange={e => set("notes", titleCaseTR(e.target.value))}
                          className="mt-2 rounded-none sm:rounded-sm" rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} data-testid="save-supplier"
                      className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
                {editing ? "Güncelle" : "Ekle"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      {/* Kaydedilmemiş firmalar (serbest metin) */}
      {unregistered.length > 0 && (
        <Card className="mb-6 rounded-none sm:rounded-sm border-amber-300 bg-amber-50/60 shadow-sm" data-testid="unregistered-panel">
          <div className="p-5 border-b border-amber-200 flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-amber-500">
                <Warning size={18} weight="duotone" className="text-white" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-800">Dikkat</div>
                <div className="text-lg font-bold text-slate-900 font-heading">Kaydedilmemiş Firmalar</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  Malzeme girişlerinde serbest metinle yazılmış, henüz Firmalar listesine eklenmemiş firmalar.
                </div>
              </div>
            </div>
            <div className="text-3xl font-black text-amber-700 font-heading">{unregistered.length}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-amber-200 bg-white/50">
                  <th className="text-left py-2 px-4 font-bold">Firma Adı</th>
                  <th className="text-right py-2 px-4 font-bold">Kullanım</th>
                  <th className="text-left py-2 px-4 font-bold hidden md:table-cell">Son Tarih</th>
                  <th className="text-left py-2 px-4 font-bold hidden lg:table-cell">Son Şantiye</th>
                  <th className="text-right py-2 px-4 font-bold">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {unregistered.map((u, i) => (
                  <tr key={u.name + i} className={i % 2 ? "bg-white/40" : "bg-white/70"} data-testid={`unregistered-row-${i}`}>
                    <td className="py-2.5 px-4 font-medium text-slate-900">{u.name}</td>
                    <td className="py-2.5 px-4 text-right font-mono font-bold text-slate-700">{u.count}</td>
                    <td className="py-2.5 px-4 text-slate-600 hidden md:table-cell">{u.last_date || "-"}</td>
                    <td className="py-2.5 px-4 text-slate-600 hidden lg:table-cell">{u.last_site || "-"}</td>
                    <td className="py-2.5 px-4 text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-none sm:rounded-sm h-8 text-xs border-slate-300"
                          onClick={() => openNewFromName(u.name)}
                          data-testid={`unregistered-edit-${i}`}
                        >
                          <PencilSimple size={12} className="mr-1" /> Detay Gir
                        </Button>
                        <Button
                          size="sm"
                          className="rounded-none sm:rounded-sm h-8 text-xs bg-[#1B4776] hover:bg-[#153859] text-white"
                          onClick={() => quickRegister(u.name)}
                          data-testid={`unregistered-quick-${i}`}
                        >
                          <FloppyDisk size={12} className="mr-1" /> Hızlı Kaydet
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Tip filtresi + arama */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button onClick={() => setFilter("all")}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold border ${
                  filter === "all" ? "bg-[#1B4776] text-white border-[#1B4776]" : "bg-white text-slate-600 border-slate-300 hover:border-[#1B4776]"
                }`}>
          Tümü ({rows.length})
        </button>
        {Object.entries(TYPES).map(([v, t]) => {
          const count = rows.filter(r => (r.supplier_type || "tedarikci") === v).length;
          return (
            <button key={v} onClick={() => setFilter(v)} data-testid={`filter-${v}`}
                    className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold border ${
                      filter === v ? "bg-[#1B4776] text-white border-[#1B4776]" : "bg-white text-slate-600 border-slate-300 hover:border-[#1B4776]"
                    }`}>
              {t.label} ({count})
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2 min-w-[240px]">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="rounded-none sm:rounded-sm h-9 w-[180px]" data-testid="supplier-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name_asc">Ada Göre (A → Z)</SelectItem>
              <SelectItem value="name_desc">Ada Göre (Z → A)</SelectItem>
              <SelectItem value="usage_desc">Kullanıma Göre (Çok → Az)</SelectItem>
              <SelectItem value="usage_asc">Kullanıma Göre (Az → Çok)</SelectItem>
              <SelectItem value="last_used">Son Kullanım Tarihi</SelectItem>
              <SelectItem value="type">Firma Tipine Göre</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ad, VKN, e-posta, adres…"
            data-testid="supplier-search"
            className="rounded-none sm:rounded-sm h-9"
          />
          {q && (
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 whitespace-nowrap">
              {visible.length} sonuç
            </span>
          )}
        </div>
      </div>

      {/* Toplu Seçim Bar'ı */}
      {isAdmin && selectedIds.size > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 p-3 bg-[#1B4776] text-white rounded-none sm:rounded-sm"
             data-testid="supplier-bulk-bar">
          <span className="text-xs uppercase tracking-widest font-bold">
            {selectedIds.size} kayıt seçildi
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={clearSelection}
                    data-testid="supplier-bulk-clear"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest font-bold bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white">
              Seçimi Temizle
            </Button>
            <Button size="sm" onClick={bulkDelete} disabled={bulkDeleting}
                    data-testid="supplier-bulk-delete"
                    className="rounded-none sm:rounded-sm bg-red-600 hover:bg-red-700 text-white text-xs uppercase tracking-widest font-bold">
              <TrashSimple size={14} className="mr-1.5" />
              {bulkDeleting ? "Siliniyor..." : "Seçilenleri Sil"}
            </Button>
          </div>
        </div>
      )}

      {/* Tümünü Seç butonu (admin) */}
      {isAdmin && visible.length > 0 && (
        <div className="mb-3 flex items-center gap-3 text-[10px] uppercase tracking-widest font-bold text-slate-500">
          <button
            data-testid="supplier-select-all"
            onClick={() => {
              const allSelected = visible.every(r => selectedIds.has(r.id));
              if (allSelected) clearSelection();
              else selectAllVisible(visible);
            }}
            className="hover:text-[#1B4776]"
          >
            {visible.every(r => selectedIds.has(r.id)) && visible.length > 0
              ? "Seçimi Kaldır"
              : "Tümünü Seç"}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((s) => {
          const t = TYPES[s.supplier_type || "tedarikci"];
          const Icon = t.icon;
          return (
            <Card key={s.id} className={`p-6 rounded-none sm:rounded-sm border shadow-sm bg-white ${selectedIds.has(s.id) ? "border-[#1B4776] ring-2 ring-[#1B4776]/30" : "border-slate-200"}`} data-testid={`supplier-card-${s.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-2">
                  {isAdmin && (
                    <Checkbox
                      checked={selectedIds.has(s.id)}
                      onCheckedChange={() => toggleOne(s.id)}
                      data-testid={`supplier-check-${s.id}`}
                      className="mt-2"
                    />
                  )}
                  <div className={`w-11 h-11 flex items-center justify-center ${t.color}`}>
                    <Icon size={22} weight="duotone" />
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(s)} className="w-8 h-8 flex items-center justify-center hover:bg-slate-100">
                    <PencilSimple size={14} className="text-slate-600" />
                  </button>
                  {isAdmin && (
                    <button onClick={() => del(s.id)} className="w-8 h-8 flex items-center justify-center hover:bg-red-50">
                      <TrashSimple size={14} className="text-red-600" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mb-2">
                <span className={`inline-block text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 ${t.color}`}>
                  {t.label}
                </span>
              </div>
              <div className="text-lg font-bold text-slate-900 font-heading">{s.name}</div>
              {s.title && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{s.title}</div>}
              <div className="mt-4 space-y-1 text-xs text-slate-500">
                {s.tax_number && <div><span className="font-bold text-slate-700">VKN:</span> <span className="font-mono">{s.tax_number}</span></div>}
                {s.tax_office && <div><span className="font-bold text-slate-700">V.D.:</span> {s.tax_office}</div>}
                {s.phone && <div><span className="font-bold text-slate-700">Tel:</span> {s.phone}</div>}
                {s.email && <div className="truncate"><span className="font-bold text-slate-700">E-posta:</span> {s.email}</div>}
                {s.website && <div className="truncate"><span className="font-bold text-slate-700">Web:</span> {s.website}</div>}
                {(s.city || s.district) && (
                  <div className="flex items-center gap-1">
                    <MapPin size={12} className="text-slate-400" />
                    <span className="font-bold text-slate-700">Konum:</span> {[s.city, s.district].filter(Boolean).join(" / ")}
                  </div>
                )}
                {s.address && <div className="line-clamp-2"><span className="font-bold text-slate-700">Adres:</span> {s.address}</div>}
                <div className="pt-1 flex items-center gap-3 text-[10px] uppercase tracking-widest font-bold">
                  <span className="text-slate-500">Kullanım:</span>
                  <span className="text-[#1B4776] font-mono">{s.usage_count || 0}</span>
                  {s.last_used_date && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span className="text-slate-500">Son:</span>
                      <span className="text-slate-700 font-mono normal-case tracking-normal">{s.last_used_date}</span>
                    </>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
        {visible.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-500">Bu tipe uygun firma yok.</div>
        )}
      </div>
    </Shell>
  );
}
